import {
  navigateBrowserToLegacyTarget,
  resolveLegacyRouteTarget,
} from '@/router/legacy-route-adapter';
import { openPopupMfe } from '@/mfe/popup-store';
import { getTracker } from '@/telemetry/tracker';
import { pushToast } from '@/ui/toast-store';
import { getCurrentUserType } from '@/auth/user-type';
import { isEnvFlagTrue } from '@/config/env-flags';

function openExternal(url: string | undefined, missingMessage: string): void {
  if (!url) {
    pushToast({ variant: 'info', message: missingMessage });
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

function openSupportTarget(target: string): void {
  const resolution = resolveLegacyRouteTarget(target);
  getTracker().event('Header-help-route-clicked', { target, supported: resolution.supported });
  if (!resolution.supported) {
    pushToast({ variant: 'info', message: resolution.reason ?? 'Support form is not available.' });
    return;
  }
  if (resolution.kind === 'popup') {
    openPopupMfe(resolution.appId, resolution.subPath);
    return;
  }
  if (resolution.kind === 'external') {
    window.open(resolution.url, '_blank', 'noopener,noreferrer');
    return;
  }
  navigateBrowserToLegacyTarget(target);
}

/**
 * Self-help documentation URL, chosen by user type. Mirrors legacy
 * `selfHelpDocumentationUrl{Internal,External}User`. The shell exposes the
 * user type via the `EY_RAPToR_UserType` global (defaults to 'internal').
 */
function selfHelpDocsUrl(): string | undefined {
  const userType = getCurrentUserType();
  return userType === 'external'
    ? import.meta.env.VITE_SELF_HELP_DOCS_URL_EXTERNAL
    : import.meta.env.VITE_SELF_HELP_DOCS_URL;
}

export function HelpPanel() {
  // Legacy `serviceNowRequestForm` feature flag gates the Create Support/
  // Feedback Ticket button: `header-help.component.ts` sets
  // `isDisabled: !isServiceNowRequestsEnabled`, and the flag is `false` in
  // dev/QA — so the button is DISABLED there. The STF ticket form is a popup
  // MFE only wired where the flag is on, so mirror that: enabled only when
  // VITE_SERVICE_NOW_REQUEST_FORM === 'true'.
  const serviceNowEnabled = isEnvFlagTrue(import.meta.env.VITE_SERVICE_NOW_REQUEST_FORM);
  return (
    <div className="header-utility-panel__help">
      <div className="header-utility-panel__help-icon">
        <svg
          width="100px"
          height="100px"
          viewBox="0 0 100 100"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
        >
          <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
            <g transform="translate(-115.000000, -175.000000)" fill="#FFFFFF" fillRule="nonzero">
              <g>
                <path d="M206.620382,175 L215,185.449882 L206.682916,197.099448 L166.648,197.099 L166.648,199.861 L170.494505,199.861878 L170.494505,221.961326 L166.648,221.961 L166.648,224.171 L206.620382,224.171271 L215,234.621152 L206.682916,246.270718 L166.648,246.27 L166.648,272.79 L180.384615,272.790055 L180.384615,275 L138.076923,275 L138.076923,272.790055 L157.857,272.79 L157.857,246.27 L154.56044,246.270718 L154.56044,224.171271 L157.857,224.171 L157.857,221.961 L122.668445,221.961326 L115,211.541216 L122.637146,199.861878 L157.857,199.861 L157.857,197.099 L154.56044,197.099448 L154.56044,175 L206.620382,175 Z M164.45,246.27 L160.054,246.27 L160.054,272.79 L164.45,272.79 L164.45,246.27 Z M195.962356,226.381215 L157.034533,226.381215 L157.034533,243.808208 L195.899721,243.808208 L201.255037,234.52644 L195.962356,226.381215 Z M205.651097,226.381215 L198.575,226.381 L204.010989,234.463299 L198.557,243.808 L205.588563,243.808208 L212.185949,234.558011 L205.651097,226.381215 Z M190.274725,234.116022 L190.274725,236.325967 L165.549451,236.325967 L165.549451,234.116022 L190.274725,234.116022 Z M164.45,221.961 L160.054,221.961 L160.054,224.171 L164.45,224.171 L164.45,221.961 Z M129.916,202.222 L123.889137,202.222931 L117.87958,211.478255 L123.857837,219.600274 L129.762,219.6 L124.89011,211.541216 L129.916,202.222 Z M168.147022,202.222931 L132.574828,202.222931 L127.661648,211.478255 L132.669313,219.600274 L168.147022,219.600274 L168.147022,202.222931 Z M160.604396,209.80663 L160.604396,212.016575 L138.076923,212.016575 L138.076923,209.80663 L160.604396,209.80663 Z M164.45,197.099 L160.054,197.099 L160.054,199.861 L164.45,199.861 L164.45,197.099 Z M195.962356,177.304657 L157.034533,177.304657 L157.034533,194.73165 L195.899721,194.73165 L201.255037,185.449882 L195.962356,177.304657 Z M205.651097,177.304657 L198.624,177.304 L204.010989,185.38674 L198.514,194.731 L205.588563,194.73165 L212.185949,185.449882 L205.651097,177.304657 Z M190.274725,184.944751 L190.274725,187.154696 L165.549451,187.154696 L165.549451,184.944751 L190.274725,184.944751 Z" />
              </g>
            </g>
          </g>
        </svg>
      </div>
      <div className="header-utility-panel__help-heading">
        <p className="header-utility-panel__help-heading-text">Need Help?</p>
      </div>
      <div className="header-utility-panel__help-description">
        <p className="header-utility-panel__help-description-text">
          If you need help with something, please use the button below to create a support ticket.
        </p>
      </div>
      <div className="header-utility-panel__help-button">
        <button
          type="button"
          className="header-utility-panel__help-btn-primary"
          disabled={!serviceNowEnabled}
          onClick={() => openSupportTarget('apps/STF')}
        >
          Create Support/Feedback Ticket
        </button>
      </div>
      <div className="header-utility-panel__help-btn-spacer">
        <button
          type="button"
          className="header-utility-panel__help-btn-dark"
          onClick={() =>
            openExternal(
              import.meta.env.VITE_EXISTING_TICKETS_URL,
              'Existing tickets link is not configured.',
            )
          }
        >
          View Existing Tickets
        </button>
      </div>
      <div className="header-utility-panel__help-btn-spacer">
        <button
          type="button"
          className="header-utility-panel__help-btn-dark"
          onClick={() =>
            openExternal(
              import.meta.env.VITE_PLATFORM_STATUS_URL,
              'Platform status link is not configured.',
            )
          }
        >
          View Platform Status
        </button>
      </div>
      <div className="header-utility-panel__help-btn-spacer">
        <button
          type="button"
          className="header-utility-panel__help-btn-dark"
          onClick={() =>
            openExternal(selfHelpDocsUrl(), 'Self help documentation link is not configured.')
          }
        >
          Self Help Documentation
        </button>
      </div>
      <div className="header-utility-panel__help-textlink">
        <button
          type="button"
          className="header-utility-panel__text-link"
          onClick={() => openSupportTarget('apps/STF/contact-support')}
        >
          Still having trouble?
        </button>
      </div>
    </div>
  );
}
